import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Material dropdown that shows a color swatch next to each layer's name.
 * Colors match Height Layers exactly (same hex).
 */
export const MaterialSelect = ({
  layers,
  activeId,
  onSelect,
  testId,
  allowNone = false,
  noneLabel = "None",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onDoc = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const noSelection = allowNone && !activeId;
  const active = noSelection ? null : layers.find((layer) => layer.id === activeId) || layers[0];
  const emptySwatch = {
    background: "linear-gradient(135deg, #27272a 45%, #71717a 46%, #71717a 54%, #27272a 55%)",
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      <button
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 bg-[#0d0e12] border border-zinc-800 rounded-md px-2 py-1.5 text-xs text-zinc-100 min-w-[150px] hover:border-zinc-600 transition"
      >
        <span
          data-testid="active-material-swatch"
          className="w-4 h-4 rounded-sm border border-zinc-700 flex-shrink-0"
          style={active ? { backgroundColor: active.color } : emptySwatch}
        />
        <span className="flex-1 text-left truncate">{active?.name || noneLabel}</span>
        <ChevronDown className="w-3 h-3 text-zinc-500" />
      </button>
      {open && (
        <div
          data-testid={`${testId}-menu`}
          className="absolute z-50 top-full mt-1 left-0 min-w-[200px] max-h-64 overflow-y-auto scroll-thin bg-[#131418] border border-zinc-800 rounded-lg shadow-xl shadow-black/60 p-1"
        >
          {allowNone && (
            <button
              data-testid={`${testId}-none`}
              onClick={() => {
                onSelect(null);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition ${
                noSelection
                  ? "bg-rose-500/15 text-white"
                  : "text-zinc-200 hover:bg-zinc-800/60"
              }`}
            >
              <span
                className="w-4 h-4 rounded-sm border border-zinc-700 flex-shrink-0"
                style={emptySwatch}
              />
              <span className="flex-1 text-left truncate">{noneLabel}</span>
            </button>
          )}
          {layers.map((layer) => (
            <button
              key={layer.id}
              data-testid={`material-option-${layer.name}`}
              onClick={() => {
                onSelect(layer.id);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition ${
                layer.id === activeId
                  ? "bg-rose-500/15 text-white"
                  : "text-zinc-200 hover:bg-zinc-800/60"
              }`}
            >
              <span
                className="w-4 h-4 rounded-sm border border-zinc-700 flex-shrink-0"
                style={{ backgroundColor: layer.color }}
              />
              <span className="flex-1 text-left truncate">{layer.name}</span>
              <span className="mono text-[10px] text-zinc-500">
                {layer.min}–{layer.max}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
